import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

import Header from './Header';
import WorkoutListItem from './WorkoutListItem';
import Filter from './Filter';
import Overlay from './Overlay';
import Close from './Close';
import Button from './Button';

import * as crossService from '../services/cross';

type VisibleType = {
  visible: boolean;
};

const motionIn = {
  duration: 0.7,
  ease: [0.6, -0.05, 0.01, 0.99]
};
const motionOut = {
  duration: 0.2
};
const motionList = {
  initial: {
    y: -20,
    opacity: 0
  },
  animate: {
    y: 0,
    opacity: 1,
    delay: 0.5,
    transition: {
      ...motionIn
    }
  },
  exit: {
    y: -20,
    opacity: 0,
    transition: {
      delay: 0.1,
      ...motionOut
    }
  }
};
const List = styled(motion.ul)`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-gap: 20px;
  list-style: none;
  padding: 0;
  margin: 0;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;
const ProgrammesFilterButtonContainer = styled.div`
  margin-bottom: 20px;
  text-align: right;
`;
const ProgrammesFilterButton = styled.a`
  display: inline-block;
  color: #FFFFFF;
  margin: 0 0 0 auto;
  height: 30px;
`;
const ProgrammesFilterContainer = styled.div<VisibleType>`
  position: absolute;
  top: ${props => props.visible ? '100px': '-100%'};
  left: 50%;
  width: calc(100% - 40px);
  height calc(100vh - 170px);
  transform: translateX(-50%);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;

  @media (orientation: landscape) and (max-width: 992px) {
    top: ${props => props.visible ? '101px': '-100%'};
    height calc(100vh - 161px);
  }
`;
const PlayerClose = styled.a`
  display: inline-block;
  color: #FFFFFF;
  margin: 0 0 20px auto;
  height: 30px;
`;
const ProgrammesFilterWrapper = styled.div`
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  height: 100%;
`;
const ProgrammesFilterScrollable = styled.div`
  overflow-y: auto;
  height: calc(100% - 81px);

  &::-webkit-scrollbar {
    width: 20px;
  }
  
  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #8E9EAE;
    border-radius: 20px;
    border: 6px solid transparent;
    background-clip: content-box;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background-color: #FFFFFF;
  }
`;
const ProgrammesFilterButtons = styled.div`
  padding: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: grid;
  grid-template-columns: calc(33.33% - 13.33px) calc(33.33% - 13.33px) calc(33.33% - 13.33px);
  grid-gap: 20px;
`;
const FilterList = styled(motion.ul)`
  list-style: none;
  padding: 20px 0 20px 20px;
  margin: 0;
`;
const FilterListItem = styled.li`
  & + & {
    margin-top: 15px;
  }
`;
const FilterLabel = styled.label`
  display: flex;
  line-height: 25px;
`;
const CheckBox = styled.input.attrs({ type: 'checkbox' })`
  -webkit-appearance: none;
  appearance: none;
  background-color: transparent;
  margin: 0 10px 0 0;
  color: currentColor;
  width: 25px;
  height: 25px;
  border: 2px solid currentColor;
  border-radius: 3px;
  transform: translateY(-0.075em);
  display: grid;
  place-content: center;

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
    transform: scale(0);
    transform-origin: bottom left;
    transition: 120ms transform ease-in-out;
    box-shadow: inset 1em 1em currentColor;
    background-color: currentColor;
  }

  &:checked::before {
    transform: scale(1);
  }
`;

interface IOwnProps {
  programmes: crossService.Programme[];
}

type Props = IOwnProps;

function WorkoutList(props: Props) {
  const [showFilter, setShowFilter] = React.useState<boolean>(false);
  const [showFilterOverlay, setShowFilterOverlay] = React.useState<boolean>(false);
  const [showFilterContainer, setShowFilterContainer] = React.useState<boolean>(false);
  const [showFilterClose, setShowFilterClose] = React.useState<boolean>(false);
  const [filterTemp, setFilterTemp] = React.useState<string[]>([]);
  const [filter, setFilter] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (!showFilter) {
      setShowFilterClose(false);
      setShowFilterContainer(false);

      setTimeout(() => {
        setShowFilterOverlay(false);
      }, 300);
    } else {
      setShowFilterOverlay(true);
      setFilterTemp(filter);

      setTimeout(() => {
        setShowFilterContainer(true);
      }, 10);

      setTimeout(() => {
        setShowFilterClose(true);
      }, 310);
    }
  }, [showFilter]);

  const handleFilterCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    var updatedList = [...filterTemp];

    if (e.target.checked) {
      updatedList = [...filterTemp, e.target.value];
    } else {
      updatedList.splice(filterTemp.indexOf(e.target.value), 1);
    }

    setFilterTemp(updatedList);
  };

  const saveFilter = () => {
    setFilter(filterTemp);
    setFilterTemp([]);
  };

  const restoreFilter = () => {
    setFilterTemp([]);
  };

  const resetFilter = () => {
    setFilter([]);
  };
  
  const programmes = filter.length > 0 ? props.programmes.filter((programme) => filter.every((f) => programme.sets.flatMap((set) => set.repetitions.flatMap((repetition) => repetition.exercises.flatMap((exercise) => exercise.name))).includes(f))) : props.programmes;

  return (
    <React.Fragment>
      <motion.div initial="initial" animate="animate" exit="exit">
        <Header title="Crosstraining" teaser="What do you want to train today?" />
        <ProgrammesFilterButtonContainer>
          <ProgrammesFilterButton href="#" onClick={(e) => { e.preventDefault(); setShowFilter(true); }}>
            <Filter />
          </ProgrammesFilterButton>
        </ProgrammesFilterButtonContainer>
        {programmes.length === 0 &&
          <React.Fragment>Ingen programmer</React.Fragment>
        }
        {programmes.length > 0 &&
          <List variants={motionList}>
            <WorkoutListItem key="random" programmes={programmes} />
            {programmes.map((programme) => {
              return <WorkoutListItem key={programme.id} programme={programme} />
            })}
          </List>
        }
      </motion.div>

      <Overlay visible={showFilterOverlay}>
        <ProgrammesFilterContainer visible={showFilterContainer}>
          <PlayerClose href="#" onClick={(e: React.MouseEvent) => { e.preventDefault(); setShowFilter(false); } }>
            <Close visible={showFilterClose} />
          </PlayerClose>
          <ProgrammesFilterWrapper>
            <ProgrammesFilterScrollable>
              <FilterList>
                {[...Array.from(new Set(props.programmes.flatMap((programme) => programme.sets.flatMap((set) => set.repetitions.flatMap((repetition) => repetition.exercises.flatMap((exercise) => exercise.name))))))].sort().map((exerciseName) => {
                  return (<FilterListItem key={exerciseName}>
                      <FilterLabel>
                        <CheckBox value={exerciseName} checked={filterTemp.includes(exerciseName)} onChange={handleFilterCheck} />
                        {exerciseName}
                      </FilterLabel>
                    </FilterListItem>);
                })}
              </FilterList>
            </ProgrammesFilterScrollable>
            <ProgrammesFilterButtons>
              <Button text='Nulstil' onClick={() => { resetFilter(); setShowFilter(false) }} />
              <Button text='Annuller' onClick={() => { restoreFilter(); setShowFilter(false); }} />
              <Button text='Gem' onClick={() => { saveFilter(); setShowFilter(false); }} />
            </ProgrammesFilterButtons>
          </ProgrammesFilterWrapper>
        </ProgrammesFilterContainer>
      </Overlay>
    </React.Fragment>
  );
}

export default WorkoutList;